import { createInstance, dateReviver } from '@/api';
import {
    DeliveryEntityDetailDto,
    DeliveryEntityLinkClient,
    DeliveryEntityLinkDetailDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { copyDeliveryEntityValues } from '../../common';

export interface DeliveryEntityLinkStoreState {
    items?: { key: string; value: DeliveryEntityLinkDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new DeliveryEntityLinkClient('', createInstance(), dateReviver);

export const useDeliveryEntityLinkStore = defineStore('configuration-delivery-entity-link-store', {
    state: (): DeliveryEntityLinkStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        bySpecification: (state) => {
            return (specificationId: number): DeliveryEntityLinkDetailDto[] => {
                const key = `specification-${specificationId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        updateDeliveryEntityUI(updatedDeliveryEntity: DeliveryEntityDetailDto) {
            this.items?.forEach((item) => {
                item.value?.forEach((entity) => {
                    copyDeliveryEntityValues(updatedDeliveryEntity, entity.fromEntity);
                    copyDeliveryEntityValues(updatedDeliveryEntity, entity.toEntity);
                });
            });
        },
        async getBySpecification(id: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            const key = `specification-${id}`;

            try {
                const response = await client.getBySpecification(id);

                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitylink.detail.error.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async createEntity(
            specificationId: number,
            fromEntityId: number,
            toEntityId: number,
            fromAttributeId: number,
            toAttributeId: number
        ) {
            this.reloading = true;

            try {
                await client.createEntityLink({
                    specificationId,
                    fromEntityId,
                    toEntityId,
                    fromAttributeId,
                    toAttributeId
                });

                await this.getBySpecification(specificationId, true);
                useAlertStore().setSuccess(i18n.global.t('entitylink.create.success.title'));
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitylink.create.error.title'), error);
            } finally {
                this.reloading = false;
            }
        },
        async updateEntity(
            specificationId: number,
            deliveryEntityLinkId: number,
            fromEntityId: number,
            toEntityId: number,
            deliveryEntityAttributeLinkId: number,
            fromAttributeId: number,
            toAttributeId: number
        ) {
            this.reloading = true;

            try {
                await client.updateEntityLink(deliveryEntityLinkId, {
                    deliveryEntityAttributeLinkId,
                    fromEntityId,
                    toEntityId,
                    fromAttributeId,
                    toAttributeId
                });

                await this.getBySpecification(specificationId, true);
                useAlertStore().setSuccess(i18n.global.t('entitylink.update.success.title'));
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitylink.update.error.title'), error);
            } finally {
                this.reloading = false;
            }
        },
        async deleteEntity(specificationId: number, deliveryEntityLinkId: number): Promise<boolean> {
            this.reloading = true;

            try {
                await client.deleteEntityLink(deliveryEntityLinkId);
                await this.getBySpecification(specificationId, true);
                useAlertStore().setSuccess(i18n.global.t('entitylink.delete.success.title'));
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitylink.delete.error.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(id: number) {
            const key = `specification-${id}`;

            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
